import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import stats from "./content/site-stats.json";
import { useMeta } from "../hooks/useMeta";
import { draftPath } from "./DraftLayout";
import { Shell, H2 } from "./parts";

const API = process.env.REACT_APP_BACKEND_URL;
const REVENUE = ["Under $150,000/month", "$150,000 to $250,000/month", "$250,000 to $500,000/month", "$500,000 to $1M/month", "$1M to $5M/month", "$5M to $10M/month", "$10M+/month"];
const SPEND = ["Under $30k/month", "$30k to $100k/month", "$100k to $500k/month", "$500k to $1M/month", "$1M to $5M/month", "$5M to $10M/month", "$10M+/month"];
const SERVICES = ["Paid Traffic", "Ad Creative", "Retention (Email and SMS)", "AEO"];
const CHANNELS = ["Meta", "Google", "AppLovin", "TikTok", "ChatGPT (beta)", "All of them"];
const EMPTY = { name: "", email: "", company: "", website: "", revenue: "", ad_spend: "", services: [], channels: [], message: "", heard_from: "" };

const input = "mt-2 w-full rounded-lg border border-mist bg-white px-4 py-3 text-base text-navy outline-none transition-colors focus:border-navy";
const Label = ({ children, required }) => <span className="text-xs font-bold uppercase tracking-widest text-navy/70">{children}{required && " *"}</span>;

export default function Apply() {
  useMeta({ title: "See if we're a fit | Smart Marketer Agency", description: "Tell us about your brand. If it's a fit, you book a call with Pep straight away." });
  const { search } = useLocation();
  const navigate = useNavigate();
  const ref = new URLSearchParams(search).get("ref") || "/apply";
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleIn = (key) => (v) => setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));
  const toggle = toggleIn("services");
  const toggleChannel = toggleIn("channels");
  const showChannels = form.services.includes("Paid Traffic");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      const res = await fetch(`${API}/api/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, ref }),
      });
      if (!res.ok) throw new Error("failed");
      const out = await res.json();
      window.scrollTo(0, 0);
      navigate(out.qualified ? `${draftPath("/book")}?id=${out.id}&t=${encodeURIComponent(out.booking_token)}` : draftPath("/not-yet"));
    } catch {
      setState("error");
    }
  };

  return (
    <main data-testid="draft-apply">
      <section className="bg-navy">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">Fit check</div>
          <h1 className="mt-4 text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-[56px]">Let's see if we're a fit.</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">Five minutes about your brand. If it's a fit, you book your call with Pep on the next screen. If it's not yet, we'll say so and point you somewhere useful.</p>
        </div>
      </section>

      <Shell testId="apply-body">
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr]">
          {(
            <form data-testid="apply-form" onSubmit={submit} className="rounded-2xl border border-mist bg-fog p-8 md:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block"><Label required>Name</Label><input data-testid="apply-name" required value={form.name} onChange={set("name")} className={input} /></label>
                <label className="block"><Label required>Email</Label><input data-testid="apply-email" type="email" required value={form.email} onChange={set("email")} className={input} /></label>
                <label className="block"><Label required>Brand</Label><input data-testid="apply-company" required value={form.company} onChange={set("company")} className={input} /></label>
                <label className="block"><Label required>Website</Label><input data-testid="apply-website" required value={form.website} onChange={set("website")} className={input} placeholder="https://" /></label>
                <label className="block"><Label required>Monthly revenue</Label>
                  <select data-testid="apply-revenue" required value={form.revenue} onChange={set("revenue")} className={input}>
                    <option value="">Select</option>{REVENUE.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </label>
                <label className="block"><Label required>Monthly ad spend</Label>
                  <select data-testid="apply-ad-spend" required value={form.ad_spend} onChange={set("ad_spend")} className={input}>
                    <option value="">Select</option>{SPEND.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </label>
                <p data-testid="apply-revenue-help" className="-mt-1 text-sm leading-relaxed text-navy/60 sm:col-span-2">Count all channels for both: DTC, Amazon, retail, wholesale. Not just your online store.</p>
              </div>
              <fieldset className="mt-6">
                <legend><Label>Which services are you interested in?</Label></legend>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SERVICES.map((s) => {
                    const on = form.services.includes(s);
                    return (
                      <button key={s} type="button" data-testid={`apply-service-${s.split(" ")[0].toLowerCase()}`} aria-pressed={on} onClick={() => toggle(s)}
                        className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${on ? "bg-navy text-white" : "border border-mist bg-white text-navy hover:border-navy"}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
              {showChannels && (
                <fieldset className="mt-5" data-testid="apply-channels">
                  <legend><Label>Which paid channels?</Label></legend>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {CHANNELS.map((c) => {
                      const on = form.channels.includes(c);
                      return (
                        <button key={c} type="button" data-testid={`apply-channel-${c.split(" ")[0].toLowerCase()}`} aria-pressed={on} onClick={() => toggleChannel(c)}
                          className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${on ? "bg-pink text-white" : "border border-mist bg-white text-navy hover:border-navy"}`}>
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              )}
              <label className="mt-6 block"><Label required>What do you want to fix?</Label><textarea data-testid="apply-message" required rows={5} value={form.message} onChange={set("message")} className={input} /></label>
              <label className="mt-5 block"><Label>How did you hear about us?</Label><input data-testid="apply-heard-from" value={form.heard_from} onChange={set("heard_from")} className={input} /></label>
              {state === "error" && <p data-testid="apply-error" className="mt-4 text-sm font-bold text-pink">Something went wrong. Email us at {stats.email} instead.</p>}
              <button type="submit" data-testid="apply-submit" disabled={state === "sending"} className="mt-8 rounded-full bg-pink px-8 py-4 text-base font-bold text-white transition-colors hover:bg-pink-dark disabled:opacity-60">
                {state === "sending" ? "Checking..." : "Check the fit"}
              </button>
            </form>
          )}

          <aside data-testid="apply-aside">
            <H2>What happens next.</H2>
            <ol className="mt-8 space-y-6">
              {["You tell us about the brand. If it's a fit, you book the call with Pep straight away.", "Discovery call. Where you are, what the main issues are, whether a 90-day plan makes sense.", "Second call. We present the 90-day plan and the proposal.", "Go live within 10 business days of a signed proposal."].map((t, i) => (
                <li key={t} className="flex gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy text-sm font-extrabold text-white">{i + 1}</span>
                  <span className="text-base leading-snug text-navy/80">{t}</span>
                </li>
              ))}
            </ol>
            <div className="mt-10 rounded-xl bg-fog p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-navy/60">A fit if</div>
              <ul className="mt-3 space-y-2">
                {stats.whoFor.fit.map((f) => <li key={f} className="flex gap-2 text-sm text-navy"><Check size={18} className="mt-0.5 shrink-0 text-green" strokeWidth={3} />{f}</li>)}
              </ul>
            </div>
            <p className="mt-6 text-sm text-navy/60">Prefer email? <a href={`mailto:${stats.email}`} className="font-bold text-link">{stats.email}</a></p>
          </aside>
        </div>
      </Shell>
    </main>
  );
}
